import { useEffect, useState } from "react";
import { IoStorefrontOutline } from "react-icons/io5";
import BranchesNumber from "../../components/BranchesSettings/BranchesNumber";
import AddNewBranch from "../../components/BranchesSettings/AddNewBranch";
import { Branch, City } from "@/types/globalTypes";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import axios from "axios";
import ErrorMessage from "@/components/ErrorMessage";

import BranchCard from "@/components/BranchesSettings/BranchCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";
import { useUserRole } from "@/hooks/useUserRole";
import PaymentStatus from "@/components/Subscribtion/PaymentStatus";

const BranchesSettings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [allBranches, setAllBranches] = useState<Branch[]>([] as Branch[]);
  const [branchesLimit, setBranchesLimit] = useState(0);
  const [allCities, setAllCities] = useState<City[]>([] as City[]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [needRefetch, setNeedRefetch] = useState(false);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const branchesUrl = `${BASE_URL}/api/dashboard/branches?locale=${lang}`;
  const citiesUrl = `${BASE_URL}/api/dashboard/data/saudi-cities`;
  const { isAdmin } = useUserRole();

  useEffect(() => {
    if (!token) return;

    setIsLoading(true);
    setError(null);
    setNeedRefetch(false);

    const fetchBranches = async () => {
      try {
        const res = await axios.get(branchesUrl, {
          headers: { Authorization: `${token}` },
        });

        if (!res.data.success) {
          setError(res.data.error);
          return;
        }

        return {
          branches: res.data?.branches || [],
          branchesLimit: res.data?.branchesLimit || 0,
        } as { branches: Branch[]; branchesLimit: number };
      } catch (error: unknown) {
        handleFetchError(error, "Failed to fetch Branches", "فشل تحديث الفروع");
        return;
      }
    };

    const fetchCities = async () => {
      try {
        const res = await axios.get(citiesUrl, {
          headers: { Authorization: `${token}` },
        });

        if (!res.data.success) {
          setError(res.data.error);
          return;
        }

        return res.data?.cities || [];
      } catch (error: unknown) {
        handleFetchError(error, "Failed to fetch Branches", "فشل تحديث الفروع");
        return;
      }
    };

    const handleFetchError = (
      error: unknown,
      enMessage: string,
      arMessage: string,
    ) => {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error || (t(enMessage, arMessage) as string);
        setError(message);
        toast.error(message);
      } else {
        console.error(error);
        const message = t("branchesSettings.unexpectedError") as string;
        setError(message);
        toast.error(message);
      }
    };

    if (branchesUrl && citiesUrl) {
      Promise.all([fetchBranches(), fetchCities()])
        .then(([branches, cities]) => {
          setAllBranches(branches?.branches || []);
          setBranchesLimit(branches?.branchesLimit || 0);
          setAllCities(cities);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          const message = t("branchesSettings.unexpectedError") as string;
          setError(message);
          toast.error(message);
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
      const invalidUrlsMessage = t("branchesSettings.invalidUrls") as string;
      setError(invalidUrlsMessage);
      toast.error(invalidUrlsMessage);
    }
  }, [branchesUrl, token, t, needRefetch, citiesUrl]);

  if (isLoading)
    return (
      <>
        <div className="flex flex-col gap-6">
          <Skeleton className="ms-auto h-[50px] w-[100px] dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[150px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
        </div>
      </>
    );
  if (error) return <ErrorMessage msg={error} />;

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="self-start text-2xl font-semibold">
            {t("branchesSettings.title")}
          </h1>

          {/* Add New Branch Modal */}
          {isAdmin && (
            <AddNewBranch
              branchesLength={allBranches.length}
              branchesLimit={branchesLimit}
              allCities={allCities}
              setNeedRefetch={setNeedRefetch}
            />
          )}
        </div>

        <div className="flex flex-col gap-5 rounded-md border border-gray-300 px-4 py-6">
          {/* Branches Number */}
          {isAdmin && (
            <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <IoStorefrontOutline className="text-xl text-gray-700" />
                <span className="text-xl">{t("branchesSettings.count")}</span>
              </div>

              {/* Branches Number Modal */}
              <BranchesNumber
                availableNumber={branchesLimit - allBranches.length || 0}
              />
            </div>
          )}

          {allBranches.length === 0 && (
            <>
              <Alert className="border-none bg-skyblue text-sidebar dark:border-none dark:bg-skyblue dark:text-sidebar">
                <AlertTitle className="text-center text-xl">
                  {" "}
                  {t("branchesSettings.noBranchesFound")}
                </AlertTitle>
                <AlertDescription className="my-5 text-center font-semibold">
                  {t("branchesSettings.noBranchesToShow")}
                </AlertDescription>
              </Alert>
            </>
          )}

          {/* Loop over Branches */}
          {allBranches.map((branch, i) => (
            <div key={i} className="flex w-full flex-col justify-center gap-5">
              <BranchCard
                deliveryEnabled={branch.deliveryEnabled}
                deliveryData={branch.deliveryData}
                key={branch._id}
                _id={branch._id}
                isMain={branch.isMain}
                locationUrl={branch.locationUrl}
                nameAR={branch.nameAR}
                nameEN={branch.nameEN}
                open={branch.open}
                orderPreparationDuration={branch.orderPreparationDuration}
                phone={branch.phone}
                userId={branch.userId}
                workingDays={branch.workingDays}
                address={branch.address}
                buildingNumber={branch.buildingNumber}
                city={branch.city}
                country={branch.country}
                district={branch.district}
                street={branch.street}
                availability={branch.availability}
                allCities={allCities}
                branchesLength={allBranches.length}
                setNeedRefetch={setNeedRefetch}
              />
              {i !== allBranches.length - 1 && (
                <hr key={i + 2} className="border-gray-200" />
              )}
            </div>
          ))}
        </div>
      </div>

      <PaymentStatus />
    </>
  );
};

export default BranchesSettings;
